import _ from "lodash";
import moment from "moment";
import React, { useState, useMemo, forwardRef } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";

import "react-datepicker/dist/react-datepicker.css";

import { MasterSelect } from "../common/MasterSelect";

import { CheckBox, FormGrid, FormGroup, FormRow, Spacer } from "@/components/common";
import { CustomDatePickerInput } from "@/components/common/CustomDatePickerInput";
import { FilterConfigGroup } from "@/components/common/SearchCondition";
import { SearchConditionConfigModal } from "@/components/common/SearchCondition/SearchConditionConfigModal";
import { SearchConditionDisplayButton } from "@/components/common/SearchCondition/SearchConditionDisplayButton";
import { SettingIconButton } from "@/components/common/SettingIconButton";
import { useSearchConditionConfig } from "@/hooks/useSearchConditionConfig";
import { AssetSearchKey, AssetSearchParams, AssetSearchKeyType } from "@/models/asset";
import { FilterConfig } from "@/models/filterConfig";
import { ConstructionMasters } from "@/models/masters";
import { SearchConditionConfigDefinition, ScreenName } from "@/models/screenConfig";

import { format } from "date-fns";

type StateProps = {
  masters: ConstructionMasters;
  categoryIds: number[];
  insertStartDate: string;
  insertEndDate: string;
  fileName: string;
  uploadTarget: number[];
};

type DispatchProps = {
  changeCategory: (categoryIds: number[]) => void;
  changeFilename: (fileName: string) => void;
  changeUploadTarget: (uploadTarget: number) => void;
  changeUploadTargets: (uploadTargets: number[]) => void;
  changeDateFrom: (date: string) => void;
  changeDateTo: (date: string) => void;
  handleSearch: (option?: { params: AssetSearchParams }) => void;
  handleClear: () => void;
  resetSearch: () => void;
};

type AssetSearchAreaProps = StateProps & DispatchProps;

const AssetSearchArea = forwardRef<HTMLDivElement, AssetSearchAreaProps>((props, ref) => {
  const { t } = useTranslation();
  const [error, setError] = useState<{ insertDate?: boolean; upload?: boolean }>({});

  const {
    masters,
    categoryIds,
    insertStartDate,
    insertEndDate,
    fileName,
    uploadTarget,
    changeCategory,
    changeFilename,
    changeUploadTarget,
    changeUploadTargets,
    handleSearch,
    handleClear,
    resetSearch,
    changeDateFrom,
    changeDateTo,
  } = props;

  // データ取り込み画面の検索条件設定の定義
  const assetSearchConditionDefinitions = useMemo<SearchConditionConfigDefinition<AssetSearchKeyType>[]>(
    () => [
      {
        itemsPerLine: 3,
        items: [
          { key: AssetSearchKey.Category, label: t("machines_category"), required: false },
          { key: AssetSearchKey.FileName, label: t("file_name"), required: false },
          { key: AssetSearchKey.InsertDate, label: t("registered_day"), required: false },
          { key: AssetSearchKey.UploadTarget, label: t("upload_destination"), required: true },
        ],
      },
    ],
    []
  );

  // 検索条件設定
  const config = useSearchConditionConfig<AssetSearchKeyType>({
    screenName: ScreenName.ASSET,
    requiredKeys: assetSearchConditionDefinitions.flatMap((v) =>
      v.items.filter((item) => item.required).map((item) => item.key)
    ),
  });
  const filterSearchConditionByConfig = (condition?: AssetSearchParams): AssetSearchParams => {
    const params = condition || {
      categoryIds,
      fileName,
      insertStartDate,
      insertEndDate,
      uploadTarget,
    };
    if (!config.map || config.showAllConditions) return params;

    return {
      categoryIds: config.map.get(AssetSearchKey.Category) ? params.categoryIds : [],
      fileName: config.map.get(AssetSearchKey.FileName) ? params.fileName : "",
      insertStartDate: config.map.get(AssetSearchKey.InsertDate) ? params.insertStartDate : "",
      insertEndDate: config.map.get(AssetSearchKey.InsertDate) ? params.insertEndDate : "",
      uploadTarget: config.map.get(AssetSearchKey.UploadTarget) ? params.uploadTarget : [],
    };
  };

  // 検索条件保存
  const getCurrentConditionLabels = () => {
    // 現在の条件を保存モーダルに表示するために編集する
    const p = filterSearchConditionByConfig();
    const formatDate = (d: string | null): string => (d ? format(new Date(d), "yyyy/MM/dd") : "");
    const names: { label: string; values: string[] | null }[] = [
      {
        label: t("machines_category"),
        values: p.categoryIds.map(
          (categoryId) => masters?.categories.find((v) => v.category_id === categoryId)?.category_name
        ),
      },
      {
        label: t("file_name"),
        values: [p.fileName],
      },
      {
        label: t("registered_day"),
        values: [
          p.insertStartDate || p.insertEndDate
            ? `${formatDate(p.insertStartDate)}〜${formatDate(p.insertEndDate)}`
            : "",
        ],
      },
      {
        label: t("upload_destination"),
        values: [
          p.uploadTarget?.includes(1) ? t("board") : "",
          p.uploadTarget?.includes(2) ? t("title") : "",
          p.uploadTarget?.includes(3) ? t("detail") : "",
        ],
      },
    ];

    return names.map((n) => ({ label: n.label, values: _.compact(n.values) })).filter((n) => n.values?.length);
  };
  const currentSearchParams = useMemo<AssetSearchParams>(() => {
    return filterSearchConditionByConfig();
  }, [categoryIds, fileName, insertStartDate, insertEndDate, uploadTarget, config.map, config.showAllConditions]);
  const handleApplyFilterConfig = (filter: FilterConfig<AssetSearchParams>) => {
    // 検索条件設定で表示している検索条件のみ復元する
    const condition = filterSearchConditionByConfig(filter.config);

    changeCategory(condition.categoryIds ?? []);
    changeFilename(condition.fileName ?? "");
    changeDateFrom(condition.insertStartDate ?? "");
    changeDateTo(condition.insertEndDate ?? "");
    changeUploadTargets(condition.uploadTarget);
  };

  const handleChangeDateFrom = (date) => {
    changeDateFrom(
      date ? `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}` : ""
    );
  };

  const handleChangeDateTo = (date) => {
    changeDateTo(
      date ? `${date.getFullYear()}-${("0" + (date.getMonth() + 1)).slice(-2)}-${("0" + date.getDate()).slice(-2)}` : ""
    );
  };

  const handleSearchClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const error: { insertDate?: boolean; upload?: boolean } = {};

    // 日付のチェック
    if ((config.map.get(AssetSearchKey.InsertDate) || config.showAllConditions) && insertStartDate && insertEndDate) {
      const sd = moment(insertStartDate);
      const ed = moment(insertEndDate);
      error.insertDate = ed.diff(sd, "days") < 0;
    }

    error.upload = uploadTarget.length < 1;
    setError(error);

    if (!error.upload) {
      if (config.showAllConditions) {
        handleSearch();
      } else {
        handleSearch({
          params: {
            categoryIds: config.map.get(AssetSearchKey.Category) ? categoryIds : [],
            fileName: config.map.get(AssetSearchKey.FileName) ? fileName : "",
            insertStartDate: config.map.get(AssetSearchKey.InsertDate) ? insertStartDate : "",
            insertEndDate: config.map.get(AssetSearchKey.InsertDate) ? insertEndDate : "",
            uploadTarget: config.map.get(AssetSearchKey.UploadTarget) ? uploadTarget : [],
          },
        });
      }
    }
  };

  const handleClearClick = () => {
    resetSearch();
    handleClear();
    setError({});
  };

  if (!config.map) return <></>;

  return (
    <div ref={ref}>
      <div className="flex justify-between bg-[#e4e4e4] w-[1200px]">
        <FilterConfigGroup
          screenName={ScreenName.ASSET}
          getCurrentConditionLabels={getCurrentConditionLabels}
          currentConfig={currentSearchParams}
          applyFilterConfig={handleApplyFilterConfig}
          data-test-id-prefix="asset"
        />
        <SettingIconButton
          className="w-[32px] p-[8px]"
          onClick={config.openSettingModal}
          data-test-id="asset-search-button-condition-save"
        />
      </div>
      <div className="search-box">
        <div className="grid gap-y-[16px]">
          <FormGrid className="grid-cols-3" display={config.show([AssetSearchKey.Category, AssetSearchKey.FileName])}>
            <FormGroup title={t("machines_category")} display={config.show([AssetSearchKey.Category])}>
              <MasterSelect
                prefix="category"
                isMulti={true}
                options={masters?.categories || []}
                value={categoryIds}
                onChange={changeCategory}
                data-test-id="asset-search-category"
              />
            </FormGroup>
            <FormGroup title={t("file_name")} display={config.show([AssetSearchKey.FileName])}>
              <input
                type="text"
                className="form-control"
                value={fileName}
                onChange={(e) => changeFilename(e.target.value)}
                data-test-id="asset-search-file-name"
              />
            </FormGroup>
          </FormGrid>
          <FormGrid
            className="grid-cols-3"
            display={config.show([AssetSearchKey.InsertDate, AssetSearchKey.UploadTarget])}
          >
            <FormGroup
              title={t("registered_day")}
              className="relative"
              display={config.show([AssetSearchKey.InsertDate])}
            >
              <div className="flex items-center space-between">
                <DatePicker
                  wrapperClassName="w-[133px]"
                  disabled={false}
                  selected={insertStartDate ? moment(insertStartDate).toDate() : null}
                  dateFormat="yyyy/MM/dd"
                  locale={t("calender_locale")}
                  onChange={handleChangeDateFrom}
                  customInput={<CustomDatePickerInput data-test-id="asset-search-registration-date-from" />}
                />
                <div className="w-30 ta-c">&nbsp;〜&nbsp;</div>
                <DatePicker
                  wrapperClassName="w-[133px]"
                  disabled={false}
                  selected={insertEndDate ? moment(insertEndDate).toDate() : null}
                  dateFormat="yyyy/MM/dd"
                  locale={t("calender_locale")}
                  onChange={handleChangeDateTo}
                  customInput={<CustomDatePickerInput data-test-id="asset-search-registration-date-to" />}
                />
              </div>
              {error.insertDate && (
                <div className="absolute form-error">
                  <p className="form-error-message w-300">{t("schedule_alert")}</p>
                </div>
              )}
            </FormGroup>
            <FormGroup
              className="relative"
              titleClassName="w-[116px]"
              title={t("upload_destination")}
              display={config.show([AssetSearchKey.UploadTarget])}
            >
              <div className="flex">
                <CheckBox
                  data-test-id="asset-search-upload-filter1"
                  checked={_.includes(uploadTarget, 1)}
                  label={t("board")}
                  onChange={() => changeUploadTarget(1)}
                />
                <Spacer x="15px" />
                <CheckBox
                  data-test-id="asset-search-upload-filter2"
                  checked={_.includes(uploadTarget, 2)}
                  label={t("title")}
                  onChange={() => changeUploadTarget(2)}
                />
                <Spacer x="15px" />
                <CheckBox
                  data-test-id="asset-search-upload-filter3"
                  checked={_.includes(uploadTarget, 3)}
                  label={t("detail")}
                  onChange={() => changeUploadTarget(3)}
                />
              </div>
              {error.upload && (
                <div className="absolute form-error">
                  <p className="form-error-message w-230">{t("set_upload_target")}</p>
                </div>
              )}
            </FormGroup>
          </FormGrid>
          <FormRow className="justify-between mt-[16px]">
            <Spacer x="210px" />
            <SearchConditionDisplayButton
              isExpanded={config.showAllConditions}
              onChange={config.toggleDisplayOptionalConditions}
              data-test-id="asset-search-button-condition"
            />
            <div className="flex gap-x-4">
              <button data-test-id="asset-search-button-reset" className="btn btn-gray" onClick={handleClearClick}>
                {t("reset")}
              </button>
              <button data-test-id="asset-search-button--search" className="btn btn-blue" onClick={handleSearchClick}>
                {t("search")}
              </button>
            </div>
          </FormRow>
        </div>
      </div>
      {config.showSettingModal && (
        <SearchConditionConfigModal<AssetSearchKeyType>
          title={t("search_condition_config")}
          configMap={config.map}
          definitions={assetSearchConditionDefinitions}
          onClose={config.closeSettingModal}
          onSubmit={(c) => config.saveSearchItemConfig(c)}
        />
      )}
    </div>
  );
});

AssetSearchArea.displayName = "AssetSearchArea";

export default AssetSearchArea;
